/** Roles and the module × action permission matrix (M02-02, M02-03). */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Alert, Button, Card, Field, PageHeader, Skeleton, StatusChip, inputClass } from '../../components/ui';
import { useRoles, type RoleRow } from './UsersPage';

interface Permission {
  key: string;
  label: string;
  module: string;
}

const MODULE_LABEL: Record<string, string> = {
  dashboard: 'Dashboard',
  barangay: 'Barangay settings',
  users: 'Users & roles',
  audit: 'Audit log',
  officials: 'Officials & staff',
  residents: 'Residents & households',
  documents: 'Certificates & clearances',
  business: 'Business clearance',
  blotter: 'Blotter & incidents',
  kp: 'Katarungang Pambarangay',
  social: 'Social services',
  drrm: 'Disaster risk reduction',
  legislation: 'Legislation & governance',
  finance: 'Finance & treasury',
  property: 'Property & inventory',
  projects: 'Projects & procurement',
  portal: 'Citizen portal',
  reports: 'Reports',
  privacy: 'Data privacy',
};

export function RolesPage(): JSX.Element {
  const { can } = useAuth();
  const roles = useRoles();
  const catalogue = useQuery({ queryKey: ['permissions'], queryFn: () => api<Permission[]>('/permissions') });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const selected = creating ? null : roles.data?.find((r) => r.id === selectedId) ?? roles.data?.[0] ?? null;
  const manage = can('users.manage');

  return (
    <>
      <PageHeader
        title="Roles & Permissions"
        description="Built-in roles follow the standard segregation of duties. Make a custom role when a job doesn't fit one."
        actions={manage && <Button onClick={() => setCreating(true)}><Plus aria-hidden="true" className="h-4 w-4" /> New role</Button>}
      />
      {(roles.isLoading || catalogue.isLoading) && <Skeleton className="h-96 w-full" />}
      {roles.data && catalogue.data && (
        <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
          <Card className="h-fit">
            <ul className="space-y-1">
              {roles.data.map((r) => (
                <li key={r.id}>
                  <button
                    className={`w-full rounded-lg px-3 py-2 text-left text-sm ${selected?.id === r.id && !creating ? 'bg-navy text-white' : 'hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                    onClick={() => { setCreating(false); setSelectedId(r.id); }}
                  >
                    <span className="font-medium">{r.name}</span>
                    <span className="block text-xs opacity-70">{r._count.memberships} user{r._count.memberships === 1 ? '' : 's'}</span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
          <RoleEditor key={creating ? 'new' : selected?.id} role={selected} catalogue={catalogue.data} editable={manage && selected?.name !== 'Owner'} onSaved={(id) => { setCreating(false); setSelectedId(id); }} />
        </div>
      )}
    </>
  );
}

function RoleEditor({ role, catalogue, editable, onSaved }: { role: RoleRow | null; catalogue: Permission[]; editable: boolean; onSaved: (id: string) => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [perms, setPerms] = useState<Set<string>>(new Set(role?.permissions ?? ['dashboard.view']));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const modules = useMemo(() => {
    const groups = new Map<string, Permission[]>();
    for (const p of catalogue) groups.set(p.module, [...(groups.get(p.module) ?? []), p]);
    return Array.from(groups.entries());
  }, [catalogue]);

  const save = useMutation({
    mutationFn: () => {
      const body = { name, description, permissions: Array.from(perms) };
      return role ? api<RoleRow>(`/roles/${role.id}`, { method: 'PATCH', body }) : api<RoleRow>('/roles', { method: 'POST', body });
    },
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      setError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onSaved(r.id);
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Could not save.'),
  });

  const toggle = (key: string): void =>
    setPerms((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const isNew = !role;
  const canEdit = isNew || editable;

  return (
    <Card
      title={isNew ? 'New role' : role.name}
      description={role?.name === 'Owner' ? 'The Owner always has full access and cannot be edited.' : role?.isSystem ? 'Built-in role: its name is fixed, its permissions can be adjusted.' : undefined}
      actions={canEdit && <Button busy={save.isPending} onClick={() => save.mutate()}>Save</Button>}
    >
      {error && <div className="mb-4"><Alert tone="critical">{error}</Alert></div>}
      {saved && <div className="mb-4"><Alert tone="ok">Saved.</Alert></div>}
      <div className="mb-5 grid gap-4 md:grid-cols-3">
        <Field label="Name" required>
          <input className={inputClass} value={name} disabled={!canEdit || role?.isSystem} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Access level" hint="Set from the permissions. Levels 1–4 must use two-step sign-in.">
          <p className="py-2 text-sm font-medium">{role ? `Level ${role.level}${role.level <= 4 ? ' · two-step sign-in' : ''}` : 'Set on save'}</p>
        </Field>
        <Field label="Description">
          <input className={inputClass} disabled={!canEdit} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </div>
      <div className="grid gap-x-6 gap-y-4 md:grid-cols-2 xl:grid-cols-3">
        {modules.map(([module, items]) => (
          <fieldset key={module}>
            <legend className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">{MODULE_LABEL[module] ?? module}</legend>
            <ul className="space-y-1">
              {items.map((p) => (
                <li key={p.key}>
                  <label className="flex items-start gap-2 text-sm">
                    <input type="checkbox" className="mt-0.5 h-4 w-4" disabled={!canEdit} checked={perms.has(p.key)} onChange={() => toggle(p.key)} />
                    <span>{p.label}</span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        ))}
      </div>
      {!isNew && role.isSystem && <p className="mt-4"><StatusChip tone="info">Built-in</StatusChip></p>}
    </Card>
  );
}
